import loc, { format } from 'owa-localize';
import {
    CalendarGroupId,
    CalendarGroup,
    getCalendarGroups,
} from 'owa-calendar-surface-calendars-store';
import { PerformanceDatapoint, returnTopExecutingActionDatapoint } from 'owa-analytics';
import handleCalendarServiceError from './handleCalendarServiceError';
import renameCalendarGroupService from '../services/renameCalendarGroupService';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { findInArray } from 'owa-calendar-data-utils/lib/findInArray';
import { lazyShowCalendarNotification } from 'owa-calendar-notification-bar';
import { getUserMailboxInfo } from 'owa-client-ids';
import { ControlIcons } from 'owa-control-icons';
import { tryAgainLabel } from 'owa-locstrings/lib/strings/tryagainlabel.locstring.json';
import CalendarActionGroupIdResponse from 'owa-service/lib/contract/CalendarActionGroupIdResponse';
import * as trace from 'owa-trace';
import {
    calendarNotificationsBarRenameCalendarGroupFailed,
    calendarNotificationsBarRenameCalendarGroup,
} from './renameCalendarGroup.locstring.json';
import showCalendarErrorNotification from './showCalendarErrorNotification';

/**
 * Action to rename calendar group
 */
export let renameCalendarGroup = createLegacyActionWithDatapoint('RenameCalendarGroup', {
    customData: (userIdentity: string, calendarGroup: CalendarGroup, newGroupName: string) => {
        return { rejected: calendarGroup.displayName === newGroupName };
    },
})(async function renameCalendarGroupInternal(
    userIdentity: string,
    calendarGroup: CalendarGroup,
    newGroupName: string
): Promise<void> {
    if (calendarGroup.displayName === newGroupName) {
        return;
    }
    const dataPoint = returnTopExecutingActionDatapoint();
    const oldCalendarGroupName = calendarGroup.displayName;

    await handleCalendarServiceError(
        constructRequestCallback(userIdentity, calendarGroup, dataPoint, newGroupName),
        constructRequestFailedCallback(
            userIdentity,
            calendarGroup.id,
            calendarGroup,
            oldCalendarGroupName,
            newGroupName
        )
    );
});

function constructRequestCallback(
    userIdentity: string,
    calendarGroup: CalendarGroup,
    dataPoint: PerformanceDatapoint,
    newGroupName: string
) {
    return async () => {
        renameGroup(calendarGroup.id, calendarGroup, getCalendarGroups(), newGroupName);
        if (dataPoint) {
            dataPoint.markUserPerceivedTime(); // mark user perceived time here as soon as we dispatch the request to rename
        }

        lazyShowCalendarNotification.import().then(showCalendarNotification => {
            showCalendarNotification(
                'renameCalendarGroup',
                'Surface',
                format(loc(calendarNotificationsBarRenameCalendarGroup), newGroupName),
                { icon: ControlIcons.CheckMark }
            );
        });

        const response = await renameCalendarGroupService(
            calendarGroup,
            newGroupName,
            getUserMailboxInfo(userIdentity)
        );
        processServiceResponse(response, newGroupName, calendarGroup, getCalendarGroups());
        return response;
    };
}

function constructRequestFailedCallback(
    userIdentity: string,
    id: CalendarGroupId,
    calendarGroup: CalendarGroup,
    oldCalendarGroupName: string,
    newCalendarGroupName: string
) {
    return () => {
        const calendarGroups = getCalendarGroups();
        let foundCalendarResult = findInArray(calendarGroups, item => {
            return item.id.id == id.id && item.id.mailboxInfo.userIdentity == userIdentity;
        });

        if (foundCalendarResult) {
            // mutate the calendar object in the calendars list so that the
            // change gets reflected in selectedCalendars list as well
            calendarGroups[foundCalendarResult.index].displayName = oldCalendarGroupName;
        } else {
            trace.errorThatWillCauseAlert(`[renameCalendar] Cannot find calendar id: ${id}`);
        }

        showCalendarErrorNotification(
            'renameCalendarGroup',
            format(
                loc(calendarNotificationsBarRenameCalendarGroupFailed),
                calendarGroup.displayName
            ),
            ControlIcons.Error,
            loc(tryAgainLabel),
            () => renameCalendarGroup(userIdentity, calendarGroup, newCalendarGroupName)
        );
    };
}

function processServiceResponse(
    response: CalendarActionGroupIdResponse,
    newGroupName: string,
    calendarGroup: CalendarGroup,
    calendarGroups: CalendarGroup[]
) {
    if (response.WasSuccessful) {
        renameGroup(
            { id: response.NewGroupItemId.Id, mailboxInfo: calendarGroup.id.mailboxInfo },
            calendarGroup,
            calendarGroups,
            newGroupName
        );
    }
}

function renameGroup(
    newId: CalendarGroupId,
    calendarGroup: CalendarGroup,
    calendarGroups: CalendarGroup[],
    newGroupName: string
) {
    let newCalendarGroup: CalendarGroup = {
        id: newId,
        groupId: calendarGroup.groupId,
        groupType: calendarGroup.groupType,
        displayName: newGroupName,
    };

    let foundGroupResult = findInArray(calendarGroups, item => {
        return item.id == calendarGroup.id;
    });

    if (foundGroupResult) {
        calendarGroups.splice(foundGroupResult.index, 1, newCalendarGroup);
    } else {
        trace.errorThatWillCauseAlert(
            `[renameCalendarGroup] Cannot find calendar group with id: ${calendarGroup.id}`
        );
    }
}

export default renameCalendarGroup;
